import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, WritableSignal } from '@angular/core';
import { isEqual as radashIsEqual } from 'radash';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { LegendItemComponent } from '@connect-services/client/components/legend/legend-item/legend-item.component';
import {
    MapBaseSource,
    MapFieldInterventionSource,
    MapLayerSource,
} from '@connect-services/client/components/map/map.component';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { CUSTOM_INDICATOR_SOURCE_ID } from '@connect-services/client/services/map-click-indicator.service';
import { MapDrawService } from '@connect-services/client/services/map-draw.service';
import { MapSourceService } from '@connect-services/client/services/map-source.service';
import { SourcesService } from '@connect-services/client/services/sources.service';
import { Layer, MapboxStyle } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';

export type GroupedSource = {
    isActivated: boolean;
    isVisible: boolean;
    name: string;
    sources: MapLayerSource[];
    style: MapboxStyle[];
};

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [LegendItemComponent, LoadingComponent, RessourcesModule],
    selector: 'app-legend-list',
    templateUrl: './legend-list.component.html',
})
export class LegendListComponent {
    public readonly fieldInterventionIsVisible = computed<boolean>(() => {
        return this.fieldInterventionSources().some((source) => source.isVisible);
    });
    public readonly fieldInterventionSources: WritableSignal<MapFieldInterventionSource[]> = signal<
        MapFieldInterventionSource[]
    >([]);
    public readonly fieldInterventionVisibleIndex = computed<number>(() => {
        return this.fieldInterventionSources().findIndex((source: MapFieldInterventionSource) => source.isVisible);
    });
    public readonly groupedSources = computed<GroupedSource[]>(() => {
        const layerSourcesByName: Record<string, MapLayerSource[]> = {};
        for (const source of this.layerSources()) {
            if (!layerSourcesByName[source.name]) {
                layerSourcesByName[source.name] = [];
            }
            layerSourcesByName[source.name].push(source);
        }

        const groupedSource: GroupedSource[] = [];
        Object.entries(layerSourcesByName).forEach(([name, groupSources]) => {
            const styles: MapboxStyle[] = [];
            groupSources.forEach((source: MapLayerSource) => styles.push(...source.style));

            const filteredStyles: MapboxStyle[] = [];
            for (const style of styles) {
                if (!filteredStyles.find((filteredStyle: MapboxStyle) => radashIsEqual(filteredStyle, style))) {
                    filteredStyles.push(style);
                }
            }

            const isActivated = groupSources.some((source: MapLayerSource) =>
                this.mapDrawService.setIsActivated(source),
            );
            const isVisible = groupSources.some((source: MapLayerSource) => source.isVisible);

            groupedSource.push({
                isActivated,
                isVisible,
                name,
                sources: groupSources,
                style: filteredStyles,
            });
        });

        return groupedSource;
    });
    public readonly isLoading: WritableSignal<boolean> = signal(false);
    public readonly layerSources: WritableSignal<MapLayerSource[]> = signal<MapLayerSource[]>([]);

    protected readonly mapSourceService = inject(MapSourceService);
    protected readonly projectBloc = inject(ProjectBloc);
    protected readonly RoutePathEnum = RoutePathEnum;
    protected readonly sourcesService = inject(SourcesService);

    private readonly mapDrawService = inject(MapDrawService);

    public constructor() {
        effect(() => {
            if (!this.sourcesService.sources()) {
                return;
            }

            this.initSources();
        });
        effect(() => this.handleChangesForLayers());
    }

    private handleChangesForLayers(): void {
        if (MapSourceService.checkSourcesZoom().sourcesLegend) {
            this.updateActivatedSource();
        }

        if (MapSourceService.checkSourcesVisibility()) {
            this.updateVisibilitySource();
        }
    }

    private initSources(): void {
        this.isLoading.set(true);

        const excludedTypes = [Layer.TypeEnum.WMS, Layer.TypeEnum.WMTS];
        this.layerSources.set(
            this.sourcesService
                .sources()
                .filter((source: MapBaseSource<unknown>) => {
                    return (
                        source instanceof MapLayerSource &&
                        !excludedTypes.includes(source.data.type) &&
                        source.sourceId !== CUSTOM_INDICATOR_SOURCE_ID
                    );
                })
                .map((source: MapBaseSource<unknown>): MapBaseSource<unknown> =>
                    Object.assign({}, source),
                ) as MapLayerSource[],
        );

        this.fieldInterventionSources.set(
            this.sourcesService.sources().filter((source: MapBaseSource<unknown>) => {
                return source instanceof MapFieldInterventionSource;
            }) as MapFieldInterventionSource[],
        );
        this.isLoading.set(false);
    }

    private updateActivatedSource(): void {
        this.isLoading.set(true);

        this.layerSources.update((sources: MapLayerSource[]) => {
            return sources.map((source: MapLayerSource) => {
                source.isActivated = this.mapDrawService.setIsActivated(source);

                return source;
            });
        });

        MapSourceService.checkSourcesZoom.update((panel) => {
            panel.sourcesLegend = true;

            return panel;
        });
        this.isLoading.set(false);
    }

    private updateVisibilitySource(): void {
        this.isLoading.set(true);

        this.layerSources.update((sources: MapLayerSource[]) => {
            return sources.map((source: MapLayerSource) => {
                source.isVisible = this.mapSourceService.isSourceVisible(source);

                return source;
            });
        });

        this.fieldInterventionSources.update((sources: MapFieldInterventionSource[]) => {
            return sources.map((source: MapFieldInterventionSource) => {
                source.isVisible = this.mapSourceService.isSourceVisible(source);

                return source;
            });
        });

        MapSourceService.checkSourcesVisibility.set(false);
        this.isLoading.set(false);
    }
}
